import { useState, useRef, useEffect } from 'react';
import { Upload, AlertCircle } from 'lucide-react';
import { useLogoStore } from '@/features/application/store/logo.store';
import { logger } from '@/features/help/utils/logger';
import api from '@/features/api/axios';

interface ErrorResponse {
  message?: string;
}

interface LogoEditorProps {
  inSidebar?: boolean;
}

// Purpose: A dashboard page wrapper for the LogoEditor component
export const DashboardLogoPage = () => {
  return (
    <div className="container mx-auto">
      <h1 className="text-2xl font-bold mb-6">Company Logo</h1>
      <p className="text-gray-600 mb-6">
        Upload your company logo to be displayed on your application forms. We recommend a logo with
        dimensions of 300x100px for best results.
      </p>
      <LogoEditor />
    </div>
  );
};

export const LogoEditor = ({ inSidebar = false }: LogoEditorProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { logoUrl, setLogoUrl } = useLogoStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Log whenever logoUrl changes from the store
  useEffect(() => {
    logger.debug('logo', 'Logo URL state changed in component', { logoUrl, inSidebar });
  }, [logoUrl, inSidebar]);

  // Logo fetching has been moved to the core dashboard initialization process

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setIsUploading(true);

    try {
      logger.debug('logo', 'Uploading logo', {
        fileName: file.name,
        fileSize: `${(file.size / 1024).toFixed(2)}KB`,
        inSidebar,
      });

      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', file.name);
      formData.append('fileType', file.type);

      // Upload to server
      const { data } = await api.post('/api/logo/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Log the URL received from the server
      logger.debug('logo', 'Received logo URL from server', { url: data.url, inSidebar });

      // Update the logo URL in the store
      setIsUploading(false);
      setLogoUrl(data.url);
      // Log immediately after attempting to set the URL in the store
      logger.debug('logo', 'Called setLogoUrl in store', { url: data.url, inSidebar });
    } catch (err) {
      const errorResponse = err as ErrorResponse;
      logger.error('logo', 'Error uploading logo', {
        error: errorResponse?.message || 'Unknown error',
        inSidebar,
      });
      setError(errorResponse?.message || 'Failed to upload logo. Please try again.');
      setIsUploading(false);
    }
  };

  const UploadUI = () => (
    <div className="flex items-center justify-center w-full">
      <label
        className={`w-full cursor-pointer flex flex-col items-center justify-center gap-2 ${inSidebar ? 'py-4' : 'py-8'} border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50/5`}
      >
        {isUploading ? (
          <>
            <div className="animate-pulse text-gray-600">Uploading...</div>
            <div className="text-gray-400 text-sm">Please wait</div>
          </>
        ) : error ? (
          <>
            <AlertCircle className={`${inSidebar ? 'h-6 w-6' : 'h-8 w-8'} text-red-500`} />
            <span className="text-red-600 text-lg">Upload Failed</span>
            <span className="text-gray-600 text-sm text-center px-4">{error}</span>
            <span className="text-blue-500 text-sm mt-2">Click to try again</span>
          </>
        ) : (
          <>
            <Upload className={`${inSidebar ? 'h-6 w-6' : 'h-8 w-8'} text-gray-400`} />
            <span className={`text-gray-600 ${inSidebar ? 'text-base' : 'text-lg'}`}>
              Upload Logo
            </span>
            <span className="text-gray-400 text-xs">300x100px</span>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*"
          onChange={handleFileChange}
          disabled={isUploading}
        />
      </label>
    </div>
  );

  // If no logo, show upload UI
  if (!logoUrl) {
    if (!inSidebar) {
      return (
        <div className="max-w-6xl mx-auto px-8 pt-6">
          <div className="w-full mb-6 bg-white rounded-lg shadow-lg p-6">
            <UploadUI />
          </div>
        </div>
      );
    }
    return <UploadUI />;
  }

  // We have a logo URL, show the logo with replacement option
  const LogoDisplay = () => (
    <div className="relative group">
      <div className="flex justify-center">
        {inSidebar ? (
          <img
            src={logoUrl}
            alt="Company Logo"
            className="w-full h-auto object-contain max-h-12"
            onError={(e) => {
              logger.error('logo', 'Failed to load logo image', { logoUrl });
              (e.target as HTMLImageElement).src =
                'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YyZjJmMiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkxvZ28gTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
            }}
          />
        ) : (
          <div className="w-[200px]">
            <img
              src={logoUrl}
              alt="Company Logo"
              className="w-full h-auto object-contain max-h-12"
              onError={(e) => {
                logger.error('logo', 'Failed to load logo image', { logoUrl });
                (e.target as HTMLImageElement).src =
                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YyZjJmMiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkxvZ28gTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
              }}
            />
          </div>
        )}
      </div>

      {/* Hover Overlay */}
      <label
        className={`absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/60 
        transition-all duration-200 cursor-pointer opacity-0 group-hover:opacity-100 ${inSidebar ? 'rounded' : ''}`}
      >
        <div className="flex flex-col items-center gap-1 text-white">
          <Upload className={inSidebar ? 'h-5 w-5' : 'h-6 w-6'} />
          <span className={inSidebar ? 'text-xs font-medium' : 'text-sm font-medium'}>
            Replace{!inSidebar && ' Logo'}
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*"
          onChange={handleFileChange}
          disabled={isUploading}
        />
      </label>

      {/* Upload Progress Overlay */}
      {isUploading && (
        <div
          className={`absolute inset-0 flex items-center justify-center bg-black/60 ${inSidebar ? 'rounded' : ''}`}
        >
          <div className={`text-white ${inSidebar ? 'text-xs' : 'text-sm'}`}>Uploading...</div>
        </div>
      )}

      {/* Error Overlay */}
      {error && (
        <div
          className={`absolute inset-0 flex items-center justify-center bg-red-500/60 ${inSidebar ? 'rounded' : ''}`}
        >
          <div className={`text-white ${inSidebar ? 'text-xs' : 'text-sm'} text-center px-2`}>
            <AlertCircle
              className={inSidebar ? 'h-3 w-3 inline-block mr-1' : 'h-4 w-4 inline-block mr-1'}
            />
            {inSidebar ? 'Error' : error}
          </div>
        </div>
      )}
    </div>
  );

  // If in sidebar, just return the logo display
  if (inSidebar) {
    return <LogoDisplay />;
  }

  // Otherwise wrap it in the standard container
  return (
    <div className="max-w-6xl mx-auto px-8 pt-6">
      <div className="w-full mb-6 bg-white rounded-lg shadow-lg p-6">
        <LogoDisplay />
      </div>
    </div>
  );
};
